import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { publicAPI, authAPI } from '../services/api';
import './Login.css';

type MobileStep = 'mobile' | 'otp_only' | 'otp_password' | 'success';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [useEmail, setUseEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [securityConfig, setSecurityConfig] = useState<{
    emailLoginEnabled: boolean;
    mobileLoginEnabled: boolean;
  } | null>(null);

  // Mobile forgot-password flow: step, verified mobile (shown in lock), OTP, new password
  const [mobileStep, setMobileStep] = useState<MobileStep>('mobile');
  const [verifiedMobile, setVerifiedMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await publicAPI.getMaintenanceStatus();
        if (res.data?.success && res.data?.data?.security) {
          const sec = res.data.data.security;
          const emailLoginEnabled = sec.email_login_enabled !== false;
          const mobileLoginEnabled = sec.mobile_login_enabled !== false;
          setSecurityConfig({
            emailLoginEnabled,
            mobileLoginEnabled,
          });
          if (emailLoginEnabled && !mobileLoginEnabled) setUseEmail(true);
          if (!emailLoginEnabled && mobileLoginEnabled) setUseEmail(false);
        } else {
          setSecurityConfig({ emailLoginEnabled: true, mobileLoginEnabled: true });
        }
      } catch (_) {
        setSecurityConfig({ emailLoginEnabled: true, mobileLoginEnabled: true });
      }
    };
    load();
  }, []);

  const isMobileFlow = !useEmail || (securityConfig?.mobileLoginEnabled && !securityConfig?.emailLoginEnabled);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (useEmail && (securityConfig?.emailLoginEnabled && securityConfig?.mobileLoginEnabled)) {
      // Email-only submit: generic forgot password
      const identifier = email.trim();
      if (!identifier) {
        setMessage({ type: 'error', text: 'Please enter your email address.' });
        return;
      }
      setLoading(true);
      try {
        const res = await authAPI.forgotPassword({ email: identifier });
        if (res.data?.success) {
          setMessage({ type: 'success', text: res.data.message || 'If an account exists, you will receive instructions. Please contact your administrator if you do not.' });
          setEmail('');
        } else {
          setMessage({ type: 'error', text: res.data?.message || 'Request failed. Please try again.' });
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || err?.message || 'Request failed. Please try again or contact support.' });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (!isMobileFlow) return;

    // --- Mobile flow ---
    const mobileClean = mobile.replace(/\D/g, '').slice(-10);

    if (mobileStep === 'mobile') {
      if (mobileClean.length !== 10) {
        setMessage({ type: 'error', text: 'Please enter a valid 10-digit mobile number.' });
        return;
      }
      setLoading(true);
      try {
        const res = await authAPI.sendOtpForForgotPassword(mobileClean);
        if (res.data?.success) {
          setVerifiedMobile(mobileClean);
          setMobileStep('otp_only');
          setOtp('');
          setNewPassword('');
          setConfirmPassword('');
          setMessage({ type: 'success', text: res.data.message || 'OTP sent successfully. Enter the OTP below and click Continue.' });
        } else {
          setMessage({ type: 'error', text: res.data?.message || 'Failed to send OTP. Please try again.' });
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || err?.message || 'Failed to send OTP. Please try again.' });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mobileStep === 'otp_only') {
      if (!otp || otp.length !== 6) {
        setMessage({ type: 'error', text: 'Please enter the 6-digit OTP.' });
        return;
      }
      setLoading(true);
      try {
        const res = await authAPI.verifyOtpForPasswordReset(verifiedMobile, otp);
        if (res.data?.success) {
          setMobileStep('otp_password');
          setMessage({ type: 'success', text: res.data.message || 'OTP verified. Now enter your new password.' });
        } else {
          setMessage({ type: 'error', text: res.data?.message || 'Invalid or expired OTP. Please try again.' });
        }
      } catch (err: any) {
        setMessage({
          type: 'error',
          text: err?.response?.data?.message || err?.message || 'Invalid or expired OTP. Please check and try again.'
        });
      } finally {
        setLoading(false);
      }
      return;
    }

    if (mobileStep === 'otp_password') {
      if (!newPassword || newPassword.length < 6) {
        setMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
        return;
      }
      if (newPassword !== confirmPassword) {
        setMessage({ type: 'error', text: 'Passwords do not match.' });
        return;
      }
      setLoading(true);
      try {
        const res = await authAPI.resetPasswordWithOtp({
          mobile: verifiedMobile,
          otp,
          new_password: newPassword,
        });
        if (res.data?.success) {
          setMobileStep('success');
          setMessage({ type: 'success', text: res.data.message || 'Your password has been successfully changed.' });
        } else {
          setMessage({ type: 'error', text: res.data?.message || 'Failed to reset password. Please try again.' });
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || err?.message || 'Failed to reset password. Please try again.' });
      } finally {
        setLoading(false);
      }
    }
  };

  const inputBase = 'w-full py-3 px-4 pl-12 border-2 border-violet-500/60 rounded-xl bg-violet-600/70 text-white text-base placeholder-violet-200/80 focus:border-violet-400 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed';
  const labelBase = 'block text-sm font-bold text-white uppercase tracking-wider mb-2';

  const subTitle = () => {
    if (mobileStep === 'success') return 'You can now sign in with your new password.';
    if (securityConfig?.emailLoginEnabled && !securityConfig?.mobileLoginEnabled) return 'Enter your email to request a password reset';
    if (securityConfig?.mobileLoginEnabled && !securityConfig?.emailLoginEnabled) {
      if (mobileStep === 'mobile') return 'Enter your mobile number to request a password reset';
      if (mobileStep === 'otp_only') return 'Enter the OTP sent to your mobile';
      if (mobileStep === 'otp_password') return 'Set your new password';
      return '';
    }
    return 'Enter your email or mobile to request a password reset';
  };

  return (
    <div className="min-h-screen min-w-full flex items-center justify-center p-5 relative overflow-hidden">
      <div className="fixed z-0" style={{ left: 0, top: 0, width: '100vw', height: '100vh', minWidth: '100%', minHeight: '100%', overflow: 'hidden', backgroundColor: '#0f172a' }}>
        <video autoPlay loop muted playsInline className="absolute" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', minWidth: '100%', minHeight: '100%', width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
      </div>
      <div className="fixed pointer-events-none z-[1] bg-slate-900/60" style={{ left: 0, top: 0, width: '100vw', height: '100vh' }} aria-hidden />
      <div className="fixed pointer-events-none z-[1] bg-[linear-gradient(rgba(139,92,246,0.08)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.08)_1px,transparent_1px)] bg-[size:50px_50px]" style={{ left: 0, top: 0, width: '100vw', height: '100vh' }} aria-hidden />
      <div className="w-full max-w-md relative z-10 min-h-0">
        <div className="rounded-2xl border-2 border-violet-500/50 bg-violet-900/30 backdrop-blur-xl shadow-2xl shadow-violet-900/30 p-8 md:p-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={`${process.env.PUBLIC_URL || ''}/logo.png`} alt="YuvanAPI Logo" className="w-44 h-auto sm:w-52 object-contain" />
            </div>
            <h1 className="text-2xl font-bold text-white uppercase tracking-wider m-0 mb-2">Forgot Password</h1>
            <p className="text-sm text-violet-300/90 m-0">{subTitle()}</p>
            <div className="w-28 h-0.5 bg-gradient-to-r from-transparent via-violet-400 to-transparent mx-auto mt-4 rounded" />
          </div>

          {message && (
            <div className={`flex items-start gap-3 p-4 rounded-xl border-2 mb-5 ${message.type === 'success' ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-200' : 'border-red-500/50 bg-red-500/20 text-red-200'} text-sm whitespace-pre-line`}>
              <span className="flex-1">{message.text}</span>
              <button type="button" onClick={() => setMessage(null)} className="flex-shrink-0 p-1 rounded hover:opacity-80" aria-label="Dismiss">✕</button>
            </div>
          )}

          {mobileStep === 'success' ? (
            <div className="mt-6 text-center">
              <p className="text-violet-200 mb-6">Your password has been successfully changed. You can now log in with your new password.</p>
              <Link to="/login" className="inline-block w-full py-3.5 px-5 rounded-xl bg-violet-500/50 border-2 border-violet-400 text-white font-bold text-sm uppercase tracking-wider hover:bg-violet-500/70 hover:border-violet-300 text-center">
                Back to Login
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6">
              {securityConfig?.emailLoginEnabled && securityConfig?.mobileLoginEnabled && (
                <div className="flex gap-2 p-1.5 rounded-xl bg-violet-500/10 border border-violet-500/30 mb-6">
                  <button type="button" onClick={() => { setUseEmail(true); setMessage(null); setMobileStep('mobile'); }} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold uppercase transition-all ${useEmail ? 'bg-violet-500/40 text-white border-2 border-violet-400' : 'text-violet-300/80 border-2 border-transparent hover:text-white'}`}>Email</button>
                  <button type="button" onClick={() => { setUseEmail(false); setMessage(null); setMobileStep('mobile'); }} className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-semibold uppercase transition-all ${!useEmail ? 'bg-violet-500/40 text-white border-2 border-violet-400' : 'text-violet-300/80 border-2 border-transparent hover:text-white'}`}>Mobile</button>
                </div>
              )}

              {(useEmail && (securityConfig?.emailLoginEnabled && securityConfig?.mobileLoginEnabled)) ? (
                <div className="mb-5 relative">
                  <label htmlFor="forgot-email" className={labelBase}>Email Address</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5z"/><path d="M20 22v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                    </span>
                    <input id="forgot-email" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" disabled={loading} className={inputBase} />
                  </div>
                </div>
              ) : (
                <>
                  {(mobileStep === 'otp_only' || mobileStep === 'otp_password') && (
                    <div className="mb-5 relative">
                      <label className={labelBase}>Mobile Number</label>
                      <div className="relative rounded-xl border-2 border-violet-500/60 bg-violet-800/60 flex items-center gap-3 py-3 px-4">
                        <span className="flex-shrink-0 w-10 h-10 rounded-full bg-violet-600/80 flex items-center justify-center text-violet-200" aria-hidden>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </span>
                        <span className="text-violet-200 font-medium tabular-nums">{verifiedMobile}</span>
                      </div>
                    </div>
                  )}
                  {mobileStep === 'mobile' && (
                    <div className="mb-5 relative">
                      <label htmlFor="forgot-mobile" className={labelBase}>Mobile Number</label>
                      <div className="relative rounded-xl border-2 border-violet-500/60 bg-violet-600/70">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 16.92v3.02a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3.02a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                        <input id="forgot-mobile" type="tel" autoComplete="off" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile number" maxLength={10} disabled={loading} className="login-input-mobile w-full py-3 px-4 pl-12 rounded-xl bg-transparent border-0 text-white text-base placeholder-violet-200/80 focus:ring-2 focus:ring-violet-400/40 focus:outline-none disabled:opacity-60" />
                      </div>
                    </div>
                  )}
                  {mobileStep === 'otp_only' && (
                    <div className="mb-5 relative">
                      <label htmlFor="forgot-otp" className={labelBase}>Enter OTP</label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7C7 5.67 8.46 2 12 2s5 3.67 5 5v4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                        </span>
                        <input id="forgot-otp" type="text" autoComplete="one-time-code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6-digit OTP" maxLength={6} disabled={loading} className={inputBase} />
                      </div>
                    </div>
                  )}
                  {mobileStep === 'otp_password' && (
                    <>
                      <div className="mb-5 relative">
                        <label htmlFor="new-password" className={labelBase}>New Password</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                          <input id="new-password" type="password" autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="At least 6 characters" disabled={loading} className={inputBase} minLength={6} />
                        </div>
                      </div>
                      <div className="mb-5 relative">
                        <label htmlFor="confirm-password" className={labelBase}>Confirm Password</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-300 w-5 h-5 flex items-center justify-center pointer-events-none">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeLinecap="round" strokeLinejoin="round"/></svg>
                          </span>
                          <input id="confirm-password" type="password" autoComplete="new-password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Confirm new password" disabled={loading} className={inputBase} minLength={6} />
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              <button type="submit" disabled={loading} className="w-full py-3.5 px-5 rounded-xl bg-violet-500/50 border-2 border-violet-400 text-white font-bold text-sm uppercase tracking-wider cursor-pointer hover:bg-violet-500/70 hover:border-violet-300 disabled:opacity-50 flex items-center justify-center gap-2">
                {loading && <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                {loading ? 'Please wait...' : mobileStep === 'mobile' ? 'Send OTP' : mobileStep === 'otp_only' ? 'Continue' : mobileStep === 'otp_password' ? 'Reset Password' : 'Submit'}
              </button>
            </form>
          )}

          {mobileStep !== 'success' && (
            <div className="mt-6 text-center">
              <Link to="/login" className="text-violet-300 hover:text-violet-200 text-sm underline underline-offset-2">Back to Login</Link>
            </div>
          )}
          <div className="mt-8 pt-6 border-t border-violet-500/30 text-center">
            <p className="text-sm text-violet-300/80 m-0">Built by <span className="font-bold text-white">Algobasket</span></p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
